import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shuffle, Sparkles, Monitor, Globe, Check, ArrowRight,
  Headphones, Wrench, Tv,
} from 'lucide-react';
import { AssistedDeploymentModal } from '../components/AssistedDeploymentModal';
import { useCart } from '../contexts/CartContext';

const FRAGMENT = '/#contact';

const setIntent = (label) => {
  try {
    sessionStorage.setItem('lab21g.contactIntent', label);
  } catch (e) {
    // sessionStorage unavailable - degrade silently; user can still type the request.
  }
};

const PRICING = [
  {
    id: 'branded',
    sku: 'randomizer-branded',
    widgetFamily: 'team-randomizer',
    price: 79,
    title: 'With LAB21G Branding',
    blurb: 'Lowest cost. LAB21G branding stays visible on the widget.',
    features: ['Web-hosted widget URL', 'Self setup', 'OBS / TikTok LIVE Studio compatible', 'LAB21G branding shown on widget'],
    fullName: 'Team Randomizer Widget — With LAB21G Branding',
    accent: 'green',
  },
  {
    id: 'unbranded',
    sku: 'randomizer-unbranded',
    widgetFamily: 'team-randomizer',
    price: 129,
    title: 'Without LAB21G Branding',
    blurb: 'Clean look — no LAB21G branding on the widget.',
    features: ['Web-hosted widget URL', 'Self setup', 'OBS / TikTok LIVE Studio compatible', 'No LAB21G branding shown'],
    fullName: 'Team Randomizer Widget — Without LAB21G Branding',
    accent: 'cyan',
    featured: true,
  },
  {
    id: 'custom',
    sku: 'randomizer-custom',
    widgetFamily: 'team-randomizer',
    price: 199,
    title: 'Customized Branding',
    blurb: 'Branded for your business, show, or live selling brand.',
    features: ['Web-hosted widget URL', 'Self setup', 'OBS / TikTok LIVE Studio compatible', 'Custom branding for your show'],
    fullName: 'Team Randomizer Widget — With Customized Branding',
    accent: 'green',
  },
];

const WHAT_IT_DOES = [
  { icon: Shuffle, title: 'Randomizes teams or spots', text: 'Pick a winner without the awkward "let me pull up a wheel" moment mid-stream.' },
  { icon: Tv, title: 'Built for live breaks', text: 'Designed around sports card breakers, live sellers, and breakers running spot draws.' },
  { icon: Globe, title: 'Web-hosted', text: 'No installs. Just a URL. Drop it into OBS or TikTok LIVE Studio as a browser source.' },
  { icon: Monitor, title: 'Stream-clean visuals', text: 'Made to look sharp on-camera so your live looks polished and trustworthy.' },
];

export const TeamRandomizerWidget = () => {
  const [pendingItem, setPendingItem] = useState(null);
  const [replacingLineId, setReplacingLineId] = useState(null);
  const { items } = useCart();

  const startAdd = (tier) => {
    // Only one option per widget family allowed. If the customer already
    // has a different variant of this widget in the cart, ask to replace it.
    const existing = items.find((i) => i.widgetFamily === tier.widgetFamily);
    if (existing && existing.sku === tier.sku) {
      toast.info('Already in your cart', {
        description: `${tier.fullName} is already in your cart.`,
        duration: 2400,
      });
      return;
    }
    setReplacingLineId(existing ? existing.lineId : null);
    setPendingItem({
      sku: tier.sku,
      name: tier.fullName,
      price: tier.price,
      widgetFamily: tier.widgetFamily,
      defaultAssisted: existing?.assistedDeployment || false,
      replacingName: existing?.name || null,
    });
  };

  const closeModal = () => {
    setPendingItem(null);
    setReplacingLineId(null);
  };

  return (
    <div data-testid="team-randomizer-page">
      <AssistedDeploymentModal
        pendingItem={pendingItem}
        replacingLineId={replacingLineId}
        onClose={closeModal}
      />
      {/* HERO */}
      <section className="hero-gradient py-20 px-4 sm:px-6 lg:px-8 border-b border-subtle">
        <div className="max-w-5xl mx-auto text-center">
          <span className="eyebrow eyebrow-blue" data-testid="randomizer-eyebrow">Stream Widget · Team Randomizer</span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 mb-5 tracking-tight leading-[1.05]"
            data-testid="randomizer-headline"
          >
            <span className="text-strong">Team Randomizer </span>
            <span className="text-gradient-brand">Widget</span>
          </h1>
          <p className="text-base sm:text-lg text-muted max-w-2xl mx-auto mb-9" data-testid="randomizer-subhead">
            A stream-ready randomizer for live breaks, team assignments, and spot
            randomization. Web-hosted. Drop the URL into OBS or TikTok LIVE Studio
            and you&apos;re live.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                const el = document.querySelector('[data-testid="randomizer-pricing"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="btn-primary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="randomizer-hero-cta-get-started"
            >
              <span>Get Started</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={FRAGMENT}
              onClick={() => setIntent('Team Randomizer Widget — Request Custom Branding')}
              className="btn-secondary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="randomizer-hero-cta-custom"
            >
              <Sparkles className="w-4 h-4" />
              <span>Request Custom Branding</span>
            </a>
          </div>
          <div className="mt-7 text-xs text-faded inline-flex items-center gap-2 flex-wrap justify-center">
            <span className="px-2.5 py-1 rounded border border-default">No install</span>
            <span className="px-2.5 py-1 rounded border border-default">Browser source ready</span>
            <span className="px-2.5 py-1 rounded border border-default">OBS · TikTok LIVE Studio</span>
          </div>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="what-it-does">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow">What it does</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              Built for breakers, by breakers.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {WHAT_IT_DOES.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="card-surface p-6 flex space-x-4" data-testid={`feature-card-${i}`}>
                <div className="shrink-0">
                  <Icon className="w-6 h-6 accent-green" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-strong mb-1">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-y border-subtle" data-testid="randomizer-pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow eyebrow-blue">Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              Pick the version that fits your show.
            </h2>
            <p className="text-sm text-muted mt-3 max-w-xl mx-auto">
              All tiers are web-hosted and self-setup. Need a hand getting it live?
              See the assisted-setup add-on below.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PRICING.map((tier) => (
              <div
                key={tier.id}
                className={`card-surface p-7 relative ${tier.featured ? 'border-emphasis' : ''}`}
                data-testid={`pricing-tier-${tier.id}`}
                style={tier.featured ? { boxShadow: '0 0 30px -8px rgba(0, 240, 255, 0.25)' } : {}}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-7 brand-green text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border border-default font-semibold" style={{ background: 'var(--bg-base)' }}>
                    Most popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-strong mb-1" data-testid={`pricing-tier-title-${tier.id}`}>
                  {tier.title}
                </h3>
                <p className="text-xs text-muted mb-5">{tier.blurb}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-strong" data-testid={`pricing-tier-price-${tier.id}`}>
                    ${tier.price}
                  </span>
                  <span className="text-sm text-faded ml-1">one-time</span>
                </div>
                <ul className="space-y-2.5 mb-7">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start space-x-2 text-sm text-default">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.accent === 'cyan' ? 'accent-cyan' : 'accent-green'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => startAdd(tier)}
                  className={`block w-full text-center px-5 py-3 rounded-md text-sm font-semibold transition-colors ${
                    tier.featured ? 'btn-primary' : 'btn-secondary'
                  }`}
                  data-testid={`pricing-tier-cta-${tier.id}`}
                >
                  Get this option
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ASSISTED SETUP */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="assisted-setup">
        <div className="max-w-3xl mx-auto card-surface p-8 sm:p-10">
          <div className="flex items-start space-x-4 mb-5">
            <Headphones className="w-7 h-7 accent-green shrink-0" />
            <div>
              <span className="eyebrow">Add-on service</span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-strong tracking-tight">
                Need Help Getting It Live?
              </h2>
            </div>
          </div>
          <p className="text-default text-base leading-relaxed mb-3">
            Add <span className="accent-green font-semibold">1 hour of Zoom-assisted setup for $99</span>. We'll
            jump on a call, help you add the widget to OBS or TikTok LIVE Studio,
            tune the look, and confirm it&apos;s ready before your next stream.
          </p>
          <p className="text-sm text-muted leading-relaxed">
            You&apos;ll be asked if you want to add Assisted Setup at checkout — no need to select it here.
          </p>
        </div>
      </section>

      {/* CUSTOMIZATION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="customization-section">
        <div className="max-w-3xl mx-auto text-center">
          <Wrench className="w-7 h-7 accent-cyan mx-auto mb-4" />
          <span className="eyebrow eyebrow-blue">Customization</span>
          <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight mb-4">
            Need a Custom Look or Feature?
          </h2>
          <p className="text-base text-muted leading-relaxed mb-7">
            Want the widget to look a certain way, or have an idea for a new
            feature? Reach out and tell us what you have in mind. Some
            customizations may require an additional quote depending on the
            scope.
          </p>
          <a
            href={FRAGMENT}
            onClick={() => setIntent('Team Randomizer Widget — Custom look / feature request')}
            className="btn-secondary px-6 py-3 inline-flex items-center space-x-2"
            data-testid="customization-cta"
          >
            <Sparkles className="w-4 h-4" />
            <span>Tell us what you want</span>
          </a>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 hero-gradient" data-testid="randomizer-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-strong tracking-tight">
            Ready to make your live breaks look cleaner?
          </h2>
          <p className="text-base text-muted mb-8">
            Pick your Team Randomizer option above, or send us a note — we&apos;ll
            get you set up.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                const el = document.querySelector('[data-testid="randomizer-pricing"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="btn-primary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="randomizer-final-cta-start"
            >
              <span>Let&apos;s get started</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/"
              className="btn-secondary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="randomizer-final-cta-back"
            >
              <span>Back to all widgets</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamRandomizerWidget;
